/**
 * File outbox sync visite Pass — lecture SA + retry / dismiss.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import type {
  LoyaltyPassVisitOutboxDoc,
  LoyaltyPassVisitOutboxLine,
  LoyaltyPassVisitOutboxStatus,
} from '../utils/loyaltyPass';

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function mapOutboxDoc(
  id: string,
  data: Record<string, unknown>
): LoyaltyPassVisitOutboxDoc {
  return {
    id,
    businessId: String(data.businessId || ''),
    transactionGroupId: String(data.transactionGroupId || id),
    clientId: typeof data.clientId === 'string' ? data.clientId : undefined,
    passId: typeof data.passId === 'string' ? data.passId : undefined,
    ticketAmountXaf: Number(data.ticketAmountXaf) || 0,
    lines: Array.isArray(data.lines)
      ? (data.lines as LoyaltyPassVisitOutboxLine[])
      : undefined,
    status: (data.status as LoyaltyPassVisitOutboxStatus) || 'pending',
    lastErrorReason:
      typeof data.lastErrorReason === 'string' ? data.lastErrorReason : undefined,
    lastErrorAt: toDate(data.lastErrorAt),
    attemptCount: Number(data.attemptCount) || 0,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    createdByUid:
      typeof data.createdByUid === 'string' ? data.createdByUid : undefined,
    lastRetriedByUid:
      typeof data.lastRetriedByUid === 'string' ? data.lastRetriedByUid : undefined,
    clientName: typeof data.clientName === 'string' ? data.clientName : undefined,
    clientPhone: typeof data.clientPhone === 'string' ? data.clientPhone : undefined,
  };
}

export function useLoyaltyPassVisitOutbox(businessId: string | undefined) {
  return useQuery({
    queryKey: ['loyaltyPassVisitOutbox', businessId],
    enabled: Boolean(businessId),
    queryFn: async (): Promise<LoyaltyPassVisitOutboxDoc[]> => {
      if (!businessId) return [];
      const col = collection(
        db,
        COLLECTIONS.businesses,
        businessId,
        COLLECTIONS.loyaltyPassVisitOutbox
      );
      const q = query(col, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => mapOutboxDoc(d.id, d.data() as Record<string, unknown>));
    },
  });
}

export function useRetryLoyaltyPassVisitOutbox(businessId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionGroupId: string) => {
      if (!businessId) throw new Error('loyaltyPass.program.errors.NO_BUSINESS');
      const fn = httpsCallable<
        { businessId: string; transactionGroupId: string },
        { synced: boolean; reason?: string; alreadyDone?: boolean }
      >(functions, 'loyaltyPassRetryVisitOutbox');
      const res = await fn({ businessId, transactionGroupId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['loyaltyPassVisitOutbox', businessId],
      });
    },
  });
}

export function useDismissLoyaltyPassVisitOutbox(businessId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionGroupId: string) => {
      if (!businessId) throw new Error('loyaltyPass.program.errors.NO_BUSINESS');
      const fn = httpsCallable<
        { businessId: string; transactionGroupId: string },
        { dismissed: boolean }
      >(functions, 'loyaltyPassDismissVisitOutbox');
      const res = await fn({ businessId, transactionGroupId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['loyaltyPassVisitOutbox', businessId],
      });
    },
  });
}
